import { useContext } from "react";

import { type ApiMagentoPriceTier } from "@jti/magento-types";
import { FormattedPrice } from "@jti/ui/components/FormattedPrice";
import { useTranslations } from "@jti/ui/providers/useTranslations";

import { PriceTiersContext } from "@product-details/providers/PriceTiersContext";

import "./ProductMultiplier.scss";

type ProductMultiplierItemType = {
  item: ApiMagentoPriceTier;
  onItemClick?: () => void;
};

const ProductMultiplierItem = ({ item, onItemClick }: ProductMultiplierItemType) => {
  const { __ } = useTranslations();
  const { singleProductPrice, setSelectedPriceTier } = useContext(PriceTiersContext);
  const { final_price, total_price, quantity, label } = item;
  const priceToDisplay = total_price ?? final_price;
  const formattedPrice = <FormattedPrice currency={priceToDisplay.currency} value={priceToDisplay.value} />;

  const formattedSingleProductPrice = singleProductPrice ? (
    <FormattedPrice currency={final_price.currency} value={singleProductPrice * quantity} />
  ) : null;

  const handleKeyDown = (event: React.KeyboardEvent<HTMLElement>) => {
    const { key } = event;
    if (key === "Enter" || key === "ArrowDown") {
      setSelectedPriceTier(item);
    }
  };

  const onClick = () => {
    setSelectedPriceTier(item);
    onItemClick?.();
  };

  return (
    <li key={quantity} className={"product-multiplier__list-item"}>
      <div
        data-testid={`${quantity}-items`}
        tabIndex={0}
        role="button"
        onClick={onClick}
        onKeyDown={handleKeyDown}
        className={"product-multiplier__list-item-title"}
      >
        <span data-testid="product-multiplier-item-label" className={"product-multiplier__list-item-title-label"}>
          {quantity} {label || __("product.label.packs")}
        </span>{" "}
        <span data-testid="product-multiplier-item-price-label" className={"product-multiplier__list-item-title-label"}>
          ({formattedPrice})
        </span>
        <div data-testid="product-multiplier-item-info-message" className={"product-multiplier__list-item-info"}>
          {quantity === 1 ? (
            <span>{__("product.label.noDiscount")}</span>
          ) : (
            formattedSingleProductPrice && (
              <>
                <div>{__("product.label.soldIndividually")}</div>
                <div>{formattedSingleProductPrice}</div>
              </>
            )
          )}
        </div>
      </div>
    </li>
  );
};

export default ProductMultiplierItem;
